import { useRef, useState } from 'react'
import { Loader2, Maximize2, RotateCw, VideoOff } from 'lucide-react'
import { ClipboardEnabledIframe } from '@/components/session/ClipboardEnabledIframe'
import { Button } from '@deepguide-ai/ui/button'

interface RunbookLiveViewProps {
  /** Normalized run status (e.g. 'running', 'completed', 'failed'). */
  status?: string
  /** Hyperbrowser live-view URL — present while the browser session is live. */
  sessionUrl?: string | null
  /** Finalized session recording — present once the run ends. */
  replayVideoUrl?: string | null
  /** True when a finished browser run's replay is still finalizing (poll). */
  replayPending?: boolean
  /** True when the replay can't be produced (recording failed / host unreachable). */
  replayUnavailable?: boolean
  /** Applied to the root section (used for the run-detail inset padding). */
  className?: string
}

/**
 * Browser observability for a runbook run: embeds the Hyperbrowser live view
 * while the session is active, then swaps to the recorded replay once the run
 * finishes. Renders nothing for non-browser runs (no session/replay URL) or
 * before the session is acquired.
 *
 * Rendered through the shared <ClipboardEnabledIframe> with the clipboard bridge
 * disabled — the bridge param + `allow-clipboard-*` sandbox flags stall / blank
 * Hyperbrowser's noVNC stream inside a sandboxed iframe.
 */
export function RunbookLiveView({
  status,
  sessionUrl,
  replayVideoUrl,
  replayPending,
  replayUnavailable,
  className,
}: RunbookLiveViewProps) {
  const isRunning = status === 'running' || status === 'awaiting_human'
  const viewerRef = useRef<HTMLDivElement>(null)
  const [reloadKey, setReloadKey] = useState(0)

  // Enlarge to fullscreen WITHOUT remounting the iframe (keeps the noVNC socket
  // alive), so the operator can watch the run up close.
  const enterFullscreen = () => {
    void viewerRef.current?.requestFullscreen?.()
  }
  // Reconnect the noVNC stream if it froze (drops the socket + reloads).
  const reloadViewer = () => setReloadKey((k) => k + 1)

  if (isRunning && sessionUrl) {
    return (
      <section className={className}>
        <div className='mb-1.5 flex items-center justify-between gap-1.5'>
          <div className='flex items-center gap-1.5'>
            <span className='relative flex h-2 w-2'>
              <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-status-blue opacity-75' />
              <span className='relative inline-flex h-2 w-2 rounded-full bg-status-blue' />
            </span>
            <span className='text-[10px] font-semibold uppercase tracking-wider text-status-blue'>
              Live browser
            </span>
          </div>
          <div className='flex items-center gap-1'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={reloadViewer}
              title='Reconnect the live view'
              className='h-7 gap-1 px-2 text-[10px] text-muted-foreground'
            >
              <RotateCw className='h-3 w-3' />
              Reload
            </Button>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={enterFullscreen}
              title='Enlarge'
              className='h-7 gap-1 px-2 text-[10px] text-muted-foreground'
            >
              <Maximize2 className='h-3 w-3' />
              Enlarge
            </Button>
          </div>
        </div>
        <div
          ref={viewerRef}
          className='aspect-[16/9] w-full overflow-hidden rounded-md border border-border bg-foreground [&:fullscreen]:aspect-auto [&:fullscreen]:rounded-none [&:fullscreen]:border-0'
        >
          <ClipboardEnabledIframe
            sessionUrl={sessionUrl}
            title='Live browser session'
            externalReloadKey={reloadKey}
            disableClipboardBridge
          />
        </div>
      </section>
    )
  }

  if (replayVideoUrl) {
    return (
      <section className={className}>
        <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-placeholder'>
          Session replay
        </div>
        <video
          src={replayVideoUrl}
          controls
          className='aspect-[16/9] w-full rounded-md border border-border bg-foreground'
        />
      </section>
    )
  }

  if (replayPending) {
    return (
      <section className={className}>
        <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-placeholder'>
          Session replay
        </div>
        <div className='flex aspect-[16/9] w-full flex-col items-center justify-center gap-2 rounded-md border border-border bg-surface-subtle text-[12px] text-text-subtle'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Generating replay video…
        </div>
      </section>
    )
  }

  if (replayUnavailable) {
    return (
      <section className={className}>
        <div className='mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-placeholder'>
          Session replay
        </div>
        <div className='flex aspect-[16/9] w-full flex-col items-center justify-center gap-2 rounded-md border border-border bg-surface-subtle px-6 text-center text-[12px] text-text-placeholder'>
          <VideoOff className='h-4 w-4' />
          Replay video unavailable for this run.
        </div>
      </section>
    )
  }

  return null
}
